'use client';

import { Map, useMap } from '@vis.gl/react-google-maps';
import React, { useState, useEffect } from 'react';

import { Box, Button, Container, useMediaQuery, useTheme } from '@mui/material';

import { checkIsCitySudair, filterPredictionFromPlaceId } from 'src/utils/helper';

import { useLocale } from 'src/locales';
import { useCurrentLocation } from 'src/lib/location';
import { getLocationByCord } from 'src/api/location/location';

import { Iconify } from 'src/components/iconify';

import { BookNow } from './book-now';
import RouteMap from './DirectionComp';

export function BookingForm({ editPermission, sx, ...other }) {
  const { t, isRTL } = useLocale();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState('1');
  const { location, getLocation } = useCurrentLocation();
  const mode = localStorage.getItem('theme-mode');
  // const { user } = useAuthContext();
  const [defaultLocation, setDefaultLocation] = useState({
    lat: 23.885942,
    lng: 45.079162,
  });
  const [colorScheme, setColorScheme] = useState('DARK');

  const [mapsData, setMapsData] = useState({
    pickupLocationDetails: '',
    delivery_1_LocationDetails: '',
    delivery_2_LocationDetails: '',
  });
  const [markersData, setMarkersData] = useState({
    pickupLocationDetails: '',
    delivery_1_LocationDetails: '',
    delivery_2_LocationDetails: '',
  });
  const [directionsData, setDirectionsData] = useState(null);
  const [isDragged, setIsDragged] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [showBookNowForm, setShowBookNowForm] = useState(false);
  const [skipZoom, setSkipZoom] = useState(false);

  useEffect(() => {
    getLocation();
    if (window.google && window.google.maps) {
      setMapsLoaded(true);
    }
    const settings = JSON.parse(localStorage.getItem('app-settings'));
    const mode = settings?.mode;
    if (mode) {
      setColorScheme(mode === 'dark' ? 'DARK' : 'LIGHT');
    } else {
      setColorScheme('DARK');
    }
  }, []);

  useEffect(() => {
    const settings = JSON.parse(localStorage.getItem('app-settings'));
    const mode = settings?.mode;
    if (mode) {
      setColorScheme(mode === 'dark' ? 'DARK' : 'LIGHT');
    } else {
      setColorScheme('DARK');
    }
  }, [mode]);
  // -------------------------------------- MAPS ---------------------------------------------------
  const map = useMap();

  // useEffect(() => {
  //   if (location) {
  //     setMapsData((prevState) => ({
  //       ...prevState,
  //       pickupLocationDetails: location,
  //     }));
  //     setMarkersData((prevState) => ({
  //       ...prevState,
  //       pickupLocationDetails: location || null,
  //     }));
  //   }
  // }, [location]);

  function currentLocation() {
    getLocation()
      .then((coords) => {
        if (coords) {
          setMapsData((prevState) => ({
            ...prevState,
            pickupLocationDetails: coords,
          }));
          setMarkersData((prevState) => ({
            ...prevState,
            pickupLocationDetails: coords || null,
          }));
        }
      })
      .catch((err) => {
        console.info('Could not get location', err);
      });
  }

  function smoothZoom(targetZoom, center, interval = 100) {
    if (!map || !center) return;

    let currentZoom = map.getZoom();
    if (typeof currentZoom !== 'number') currentZoom = 7;

    if (currentZoom === targetZoom) return;

    const zoomDirection = currentZoom < targetZoom ? 1 : -1;

    const zoomInterval = setInterval(() => {
      if (!map) {
        clearInterval(zoomInterval);
        return;
      }

      currentZoom = map.getZoom();

      // ✅ Keep the zoom centered around the same coordinate (avoid shifting)
      map.setCenter(center);
      map.setZoom(currentZoom + zoomDirection);

      if (currentZoom + zoomDirection === targetZoom) {
        clearInterval(zoomInterval);
      }
    }, interval);
  }

  useEffect(() => {
    if (
      !skipZoom &&
      map &&
      mapsData?.pickupLocationDetails?.lat &&
      mapsData?.pickupLocationDetails?.lng &&
      !mapsData?.delivery_1_LocationDetails?.lat &&
      !mapsData?.delivery_1_LocationDetails?.lng &&
      !mapsData?.delivery_2_LocationDetails?.lat &&
      !mapsData?.delivery_2_LocationDetails?.lng
    ) {
      const center = mapsData.pickupLocationDetails;
      const targetZoom = 15;

      map.panTo(center);

      // ✅ Wait until the map center actually matches our target
      const waitForPan = setInterval(() => {
        const c = map.getCenter();
        const distance = Math.abs(c.lat() - center.lat) + Math.abs(c.lng() - center.lng);

        // Once pan completed (very close)
        if (distance < 0.0001) {
          clearInterval(waitForPan);
          smoothZoom(targetZoom, center, 250);
        }
      }, 100);

      // Safety cleanup
      return () => clearInterval(waitForPan);
    }
  }, [mapsData, markersData, map, skipZoom]);

  // useEffect(() => {
  //   if (user) {
  //     updateLangOnServer(currentLang.value);
  //   }
  // }, [user]);

  const filteredPredictionFromDrag = async (newValue) => {
    if (!newValue) return null;

    const hasLocation = newValue?.geometry?.location;
    const isPlusCode = /([23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3})/i.test(
      newValue?.description || newValue?.formatted_address || ''
    );

    const result =
      isPlusCode && hasLocation ? await filterPredictionFromPlaceId(newValue) : newValue;

    const modifiedResult = {
      ...result,
      formatted_address: newValue?.description || newValue?.formatted_address || '',
    };

    return modifiedResult && Object.keys(modifiedResult).length ? modifiedResult : null;
  };

  const handleDrag = async (label, value) => {
    let selectedPrediction = {};
    const locationDetailsByCord = await getLocationByCord(`${value?.lat()},${value?.lng()}`);
    if (
      Object.keys(locationDetailsByCord || {}).length > 0 &&
      locationDetailsByCord?.predictions?.length > 0
    ) {
      // check if the city is Sudair, if yes, the function will return the city as Sudair or return the original city
      selectedPrediction =
        checkIsCitySudair(locationDetailsByCord) ||
        (await filteredPredictionFromDrag(locationDetailsByCord?.predictions?.[0])) ||
        {};
    }
    const newData =
      Object.keys(selectedPrediction || {}).length >= 1
        ? {
            ...selectedPrediction,
            geometry: {
              ...selectedPrediction?.geometry,
              location: {
                lat: Number(value?.lat()),
                lng: Number(value?.lng()),
              },
            },
            lat: Number(value?.lat()),
            lng: Number(value?.lng()),
          }
        : { lat: value?.lat(), lng: value?.lng() };

    // update mapsData
    setMapsData((prev) => ({ ...prev, [label]: newData }));
    setIsDragged(true);
  };

  const refreshMapToCurrentLocation = async () => {
    try {
      setSkipZoom(true);

      const coords = await getLocation(); // { lat, lng }

      setMapsData({
        pickupLocationDetails: coords,
        delivery_1_LocationDetails: '',
        delivery_2_LocationDetails: '',
      });

      setMarkersData({
        pickupLocationDetails: coords || null,
        delivery_1_LocationDetails: '',
        delivery_2_LocationDetails: '',
      });

      if (map && coords?.lat && coords?.lng) {
        const center = { lat: coords.lat, lng: coords.lng };
        map.panTo(center);
        // smoothZoom(15, center, 250);
        map.setZoom(15);
      }

      setTimeout(() => setSkipZoom(false), 1000);
    } catch (err) {
      console.error('Could not get location', err);
      focusToDefaultLocation();
    }
  };

  const handleResetForm = () => {
    setMapsData({
      delivery_1_LocationDetails: '',
      delivery_2_LocationDetails: '',
      pickupLocationDetails: '',
    });
    setMarkersData({
      pickupLocationDetails: '',
      delivery_1_LocationDetails: '',
      delivery_2_LocationDetails: '',
    });
    setDirectionsData(null);
    setIsDragged(false);
    setShowBookNowForm(!showBookNowForm);
    setTimeout(() => {
      focusToDefaultLocation();
    }, 500);
  };

  const focusToDefaultLocation = () => {
    if (map && defaultLocation?.lat && defaultLocation?.lng) {
      map.panTo(defaultLocation);
      map.setZoom(6); // You can adjust zoom level as needed
    }
  };

  return (
    <Box
      component="section"
      sx={[
        {
          pt: { xs: 0, md: 10 },
          pb: { xs: 0, md: 0 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container maxWidth={false} disableGutters>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: { lg: 'calc(100vh - 100px)', xs: 'auto' },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage:
                colorScheme === 'DARK'
                  ? 'linear-gradient(to bottom, transparent 10%, #000 80%)'
                  : 'linear-gradient(to bottom, transparent 50%, #000 100%)',
              pointerEvents: 'none', // ✅ It allows clicking through the overlay
              '& *': { pointerEvents: 'auto' },
              zIndex: 1,
              display: { xs: 'none', lg: 'block' },
            },
          }}
        >
          <Box sx={{ ...styles.mapContainerStyle }}>
            <Map
              defaultCenter={defaultLocation} // Example: San Francisco
              defaultZoom={7}
              disableDefaultUI={false}
              zoomControl
              streetViewControl={false}
              fullscreenControl={false}
              colorScheme={colorScheme}
              cameraControl={false}
              mapTypeControlOptions={{
                position: isRTL
                  ? window?.google?.maps?.ControlPosition?.TOP_LEFT
                  : window.google.maps.ControlPosition?.TOP_RIGHT,
              }}
              zoomControlOptions={{
                position: isRTL
                  ? window?.google?.maps?.ControlPosition?.TOP_RIGHT
                  : window?.google?.maps?.ControlPosition?.TOP_LEFT,
              }}
            >
              <RouteMap
                pickupPoint={mapsData.pickupLocationDetails}
                delivery1Point={mapsData.delivery_1_LocationDetails}
                delivery2Point={mapsData.delivery_2_LocationDetails}
                onMarkerDragEnd={(pointKey, latLng) => {
                  handleDrag(pointKey, latLng);
                }}
                onDirection={setDirectionsData}
                isSmall={isSmall}
              />
            </Map>
          </Box>

          <Container
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'end',
              height: '100%',
              position: 'relative',
              p: { xs: 2, md: 2 },
              pointerEvents: 'none', // ✅ allow dragging map
              '& *': { pointerEvents: 'auto' },
            }}
          >
            <Box
              sx={[
                {
                  width: '100%',
                  gap: 2.5,
                  display: 'flex',
                  borderRadius: 2,
                  alignItems: { md: 'flex-start' },
                  flexDirection: { xs: 'column', md: 'column' },
                  zIndex: 2,
                  position: { lg: 'absolute', xs: 'relative' },
                  bottom: { xs: '10%', lg: 'auto' },
                  // left: { xs: 'auto', lg: '2%' },
                  mx: 'auto',
                  pointerEvents: 'none', // ✅ allow dragging map
                  '& *': { pointerEvents: 'auto' },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
              ]}
            >
              {/* <AnimatedTagline /> */}

              <BookNow
                markersData={markersData}
                mapsData={mapsData}
                setMapsData={setMapsData}
                setMarkersData={setMarkersData}
                directionsData={directionsData}
                refreshMap={refreshMapToCurrentLocation}
                editPermission={editPermission}
                setIsDragged={setIsDragged}
                isDragged={isDragged}
                setDirectionsData={setDirectionsData}
                defaultLocation={defaultLocation}
                styles={styles}
                Map={Map}
                handleDrag={(label, value) => handleDrag(label, value)}
                getCurrentLocation={getLocation}
                setShowBookNowForm={setShowBookNowForm}
                currentLocation={location}
              />
            </Box>
          </Container>
        </Box>
        <Button onClick={currentLocation} variant="contained" sx={styles.locateMe}>
          <Iconify
            icon="mdi:crosshairs-gps"
            sx={{ color: 'primary.main' }}
            width={24}
            height={24}
          />
        </Button>
      </Container>
    </Box>
  );
}

const styles = {
  bookNowCard: {
    p: 3,
    borderRadius: 3,
    position: 'absolute',
    my: { xs: 1, md: 2 },
    zIndex: 2,
    width: { xs: '95%', md: 400 },
    gap: 2,
    flexDirection: 'column',
    display: 'flex',
  },
  locateMe: {
    position: 'absolute',
    bottom: { md: '15%', xs: 'unset' },
    top: { xs: '30%', md: 'unset' },
    transform: 'translateY(-50%)',
    right: 12,
    borderRadius: '50%',
    p: 1,
    minWidth: 0,
    backgroundColor: 'white',
    zIndex: 10,
  },
  mapContainerStyle: {
    width: '100%',
    height: { xs: '40vh', lg: '100%' }, // set a real height for mobile
    position: { xs: 'relative', lg: 'absolute' },
    top: 0,
    left: 0,
  },
};
